import DashboardFiltration from '../../components/machineDashboard/filtration';
import { Divider, LinearProgress, Box, Typography } from '@mui/material';
import { CustomErrorLogger } from '../../utils/CustomErrorLogger';
import ListView from '../../components/machineDashboard/ListView';
import { useState, useCallback, useRef, useEffect } from 'react';
import { isObjectEmpty } from '../../utils/isObjectEmpty';
import NoRecord from '../../components/NoRecord';
import axios from 'axios';
import '../../css/dashboard/dashboardApp.css';

export const MachineDashboard = () => {

  const scrollRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [showResponsiveFilter, setShowResponsiveFilter] = useState(false);

  const [filters, setFilters] = useState(
    JSON.parse(localStorage.getItem('machineDashboardFilters')) || {
      debouncedSearch: '',
      selectedStatus: '',
      selectedType: '',
      selectedRegion: '',
      selectedSite: '',
      selectedPole: '',
      selectedMetier: '',
      selectedYear: '',
      selectedMonth: '',
    }
  );

  const defaultSortBy = {
    name: null,
    status: null,
    type: null,
    region: null,
    site: null,
    pole: null,
    metier: null,
    cost_status: null,
    total_expenses: null,
    purchase_price: null,
  };

  const [sortBy, setSortBy] = useState(() => {
    const storedSortBy = JSON.parse(localStorage.getItem('machineDashboardSortBy')) || {};
    return { ...defaultSortBy, ...storedSortBy }; // Merge defaults with stored sortBy
  });

  const [machines, setMachines] = useState([])
  const [status, setStatus] = useState([]);
  const [types, setTypes] = useState([]);
  const [regions, setRegions] = useState([]);
  const [sites, setSites] = useState([]);
  const [poles, setPoles] = useState([]);
  const [metiers, setMetiers] = useState([]);
  const [totalMachines, setTotalMachines] = useState('');
  const [totalExpenses, setTotalExpenses] = useState('');
  const [totalPrices, setTotalPrices] = useState('');
  const [filteredMachinesCount, setFilteredMachinesCount] = useState('');
  const [machinesFetched, setMachinesFetched] = useState(0);

  const [offset, setOffset] = useState(0);
  const limit = 30;

  const filtersEmpty = isObjectEmpty(filters, ['selectedYear', 'selectedMonth']);

  useEffect(() => {
    setLoading(true)
  }, [filters, sortBy]);

  useEffect(() => {
    localStorage.setItem('machineDashboardFilters', JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    localStorage.setItem('machineDashboardSortBy', JSON.stringify(sortBy));
  }, [sortBy]);

  // Scroll to top when filters change
  useEffect(() => {
    if (scrollRef.current) {
      setOffset(0);
      scrollRef.current.scrollTo(0);
    }
  }, [filters]);

  const fetchMachines = useCallback(async () => {
    try {
      const adjustedOffset = Math.max(offset - 10, 0) || 0; // Ensure offset is not negative
      const { status, data } = await axios.get('machine', { params: { offset: adjustedOffset, limit, filters, sortBy, fromDashboard: true } });
      if (status === 200 || status === 201) {
        // Create a copy of the current files array
        const updatedMachines = [];
        // Update the fetched records in their correct positions
        data?.machines?.forEach((file, index) => { updatedMachines[adjustedOffset + index] = file });
        // Update the state with the new files array
        setLoading(false);
        setMachines(updatedMachines);
        setFilteredMachinesCount(data?.filteredMachinesCount);
        setTotalExpenses(data?.filteredMachinesTotalExpenses);
        setTotalPrices(data?.filteredMachinesTotalPrices);
        setTotalMachines(data?.totalMachinesCount);
      }
    } catch (error) {
      setLoading(false);
      console.error('Error Fetching machines:', error);
      CustomErrorLogger(error, 'fetch machines in machines page');
    }
  }, [offset, filters, sortBy]);

  const fetchMachinesFiltrationArrays = useCallback(async () => {
    try {
      const { status, data } = await axios.get('machine/getMachineFiltrationArrays', { params: { filters } });
      if (status === 200 || status === 201) {
        setRegions(data?.regionArray);
        setSites(data?.siteArray);
        setPoles(data?.poleArray);
        setMetiers([...data?.metierArray]);
        setTypes([...data?.typeArray]);
        setStatus(data?.statusArray);
      }
    } catch (error) {
      console.error('Error Fetching machines filtration arrays:', error);
      CustomErrorLogger(error, 'fetch machines filtration arrays in machines page');
    }
  }, [filters]);

  useEffect(() => {
    fetchMachines()
  }, [fetchMachines]);

  useEffect(() => {
    fetchMachinesFiltrationArrays()
  }, [fetchMachinesFiltrationArrays]);

  return (
    <Box>
      {loading ?
        <Box>
          <DashboardFiltration
            filtersEmpty={filtersEmpty}
            filters={filters}
            setFilters={setFilters}
            totalMachines={totalMachines}
            filteredMachinesCount={filteredMachinesCount}
            machinesFetched={machinesFetched}
            showResponsiveFilter={showResponsiveFilter}
            setShowResponsiveFilter={setShowResponsiveFilter}
          />
          <LinearProgress sx={{ '& .MuiLinearProgress-bar': { backgroundColor: '#EA5E00' }, backgroundColor: '#A8ADB8' }} />
        </Box> :
        filtersEmpty && machines?.length === 0 ?
          <NoRecord
            description="Aucun machine ajouté pour le moment"
            buttonTitle="Ajouter une machine"
          /> :
          <Box>
            <DashboardFiltration
              filtersEmpty={filtersEmpty}
              filters={filters}
              setFilters={setFilters}
              totalMachines={totalMachines}
              filteredMachinesCount={filteredMachinesCount}
              machinesFetched={machinesFetched}
              showResponsiveFilter={showResponsiveFilter}
              setShowResponsiveFilter={setShowResponsiveFilter}
            />

            <ListView
              scrollRef={scrollRef}
              machines={machines}
              regions={regions}
              sites={sites}
              poles={poles}
              metiers={metiers}
              types={types}
              status={status}
              filters={filters}
              setFilters={setFilters}
              sortBy={sortBy}
              setSortBy={setSortBy}
              setOffset={setOffset}
              filteredMachinesCount={filteredMachinesCount}
              setMachinesFetched={setMachinesFetched}
              showResponsiveFilter={showResponsiveFilter}
            />
            <Divider sx={{ marginTop: '20px', marginBottom: '20px' }} />
            <Box className='addInvoice_total_box'>
              <Typography className='addInvoice_total_typo'>Total prix d'achat </Typography>
              <Typography className='addInvoice_total_typo'>{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(totalPrices)}</Typography>
            </Box>
            <Box className='addInvoice_total_box'>
              <Typography className='addInvoice_total_typo'>Total dépenses </Typography>
              <Typography className='addInvoice_total_typo'>{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(totalExpenses)}</Typography>
            </Box>
          </Box>
      }
    </Box>
  );
}
